import type {
  StockSurplus,
  StockSurplusLocation,
  StockSurplusReason,
} from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Field, PanelNote } from './DetailPanels';
import {
  SurplusExplanation,
  SurplusExplanationTrigger,
} from '../../demand-forecasting/surplus/SurplusExplanation';
import { stockSurplusLimitationLabel } from './stockSurplusLimitations';
import {
  effectivePolicySourceLabel,
  surplusSalesBasisLabel,
} from './panelFormat';

type Translate = ReturnType<typeof useGT>;

const dayFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeZone: 'UTC',
});
const monthFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

/** Whole units, as every other quantity on the panel. */
function quantity(value: number): string {
  return Math.round(value).toLocaleString();
}

function day(isoDate: string): string {
  return dayFormatter.format(new Date(`${isoDate.slice(0, 10)}T00:00:00Z`));
}

/** The window is stored end-exclusive; planners read the last day included. */
function lastDayBefore(isoDate: string): string {
  const end = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() - 1);
  return dayFormatter.format(end);
}

function month(isoDate: string): string {
  return monthFormatter.format(new Date(`${isoDate.slice(0, 10)}T00:00:00Z`));
}

function reasonLabel(reason: StockSurplusReason | null, gt: Translate) {
  switch (reason) {
    case 'no_snapshot':
      return gt('No inventory snapshot.', {
        context: 'stock surplus unavailable reason',
      });
    case 'missing_commitments':
      return gt('Commitment data is unavailable.', {
        context: 'stock surplus unavailable reason',
      });
    case 'missing_policy':
      return gt('Minimum stock protection is unavailable.', {
        context: 'stock surplus unavailable reason',
      });
    case 'history_unavailable':
      return gt('Customer-sales history or its coverage is unavailable.', {
        context: 'stock surplus unavailable reason',
      });
    case 'catchment_pending':
      return gt('The full hub demand catchment is not yet available.', {
        context: 'stock surplus unavailable reason',
      });
    case 'invalid_input':
      return gt('Source quantities, units or coverage need review.', {
        context: 'stock surplus unavailable reason',
      });
    default:
      return null;
  }
}

/**
 * Locations by name, in the order given. Locations the page has no name for
 * are counted rather than printed as identifiers nobody can read.
 */
function namedList(
  ids: readonly string[],
  names: ReadonlyMap<string, string>,
  gt: Translate,
): string {
  const known = ids.flatMap((id) => {
    const name = names.get(id);
    return name ? [name] : [];
  });
  const unnamed = ids.length - known.length;
  if (unnamed === 0) return known.join(', ');
  const others = gt('{count} other locations', {
    context: 'stock surplus demand scope, locations without a known name',
    count: String(unnamed),
  });
  return known.length > 0 ? `${known.join(', ')}, ${others}` : others;
}

/** Distinct demand locations across the scope, in first-seen order. */
function demandScopeIds(result: StockSurplus): string[] {
  return [
    ...new Set(result.locations.flatMap((row) => row.demandWarehouseIds)),
  ];
}

type LocationCalculationProps = {
  row: StockSurplusLocation;
  names: ReadonlyMap<string, string>;
  /** Sales are recorded but not proven complete for any month. */
  coverageUnverified: boolean;
};

function LocationHeader({
  row,
  names,
}: Pick<LocationCalculationProps, 'row' | 'names'>) {
  const gt = useGT();
  return (
    <>
      <p className="font-medium">
        {names.get(row.warehouseId) ??
          gt('Unnamed location', {
            context: 'stock surplus breakdown, location without a known name',
          })}
      </p>
      {row.reason && <p>{reasonLabel(row.reason, gt)}</p>}
      {row.status === 'estimated' && (
        <p className="font-medium">
          <T context="stock surplus estimate status">
            Estimate from partial history
          </T>
        </p>
      )}
    </>
  );
}

function LocationCommitments({ row }: Pick<LocationCalculationProps, 'row'>) {
  return (
    <>
      <Field
        label={<T context="stock surplus calculation input">On hand</T>}
        value={row.onHand}
      />
      <Field
        label={<T context="stock surplus calculation input">Reserved</T>}
        value={row.reserved}
      />
      <Field
        label={
          <T context="stock surplus calculation input">
            Unallocated sales orders
          </T>
        }
        value={row.salesOrderDemand}
      />
      <Field
        label={
          <T context="stock surplus calculation input">
            Unacknowledged transfers
          </T>
        }
        value={row.stagedOutbound}
      />
      <Field
        label={
          <T context="stock surplus raw position">Stock minus commitments</T>
        }
        value={row.uncommittedPosition}
      />
      {row.uncommittedPosition !== null && row.uncommittedPosition < 0 && (
        <Field
          label={
            <T context="stock surplus shortage">Commitments exceed stock by</T>
          }
          value={-row.uncommittedPosition}
        />
      )}
      <Field
        label={
          <T context="stock surplus calculation input">After commitments</T>
        }
        value={row.available}
      />
    </>
  );
}

function LocationSales({
  row,
  coverageUnverified,
}: Pick<LocationCalculationProps, 'row' | 'coverageUnverified'>) {
  return (
    <>
      <Field
        label={
          coverageUnverified ? (
            <T context="stock surplus calculation input">
              Recorded customer sales, coverage unverified
            </T>
          ) : (
            <T context="stock surplus calculation input">
              Observed customer sales
            </T>
          )
        }
        value={row.observedSales}
      />
      <Field
        label={
          <T context="stock surplus calculation input">
            Months of known coverage
          </T>
        }
        value={`${row.coveredMonths.length} / 12`}
      />
      <Field
        label={
          row.status === 'estimated' ? (
            <T context="stock surplus calculation input">
              Estimated annual sales
            </T>
          ) : (
            <T context="stock surplus calculation input">Annual sales basis</T>
          )
        }
        value={row.annualSales}
      />
    </>
  );
}

function LocationProtection({ row }: Pick<LocationCalculationProps, 'row'>) {
  const gt = useGT();
  return (
    <>
      <Field
        label={
          <T context="stock surplus calculation input">Minimum protection</T>
        }
        value={row.minimumProtection}
      />
      <Field
        label={
          <T context="stock surplus calculation input">Protection set by</T>
        }
        value={
          row.minimumSource && effectivePolicySourceLabel(row.minimumSource, gt)
        }
      />
      <Field
        label={<T context="stock surplus calculation input">Retained stock</T>}
        value={row.retained}
      />
      <Field
        label={
          <T context="stock surplus calculation result">
            Stock above retention
          </T>
        }
        value={row.quantity}
      />
      <Field
        label={
          <T context="stock surplus source timestamp">Inventory snapshot</T>
        }
        value={row.snapshotDate && day(row.snapshotDate)}
      />
    </>
  );
}

function RecordedSalesCaveat({
  row,
  coverageUnverified,
}: Pick<LocationCalculationProps, 'row' | 'coverageUnverified'>) {
  return (
    <>
      {coverageUnverified && row.observedSales !== null && (
        <p>
          <T context="stock surplus recorded sales caveat">
            Recorded sales, not a proven annual basis. They cannot establish
            fully covered months, so no estimate is made from them.
          </T>
        </p>
      )}
    </>
  );
}

function LocationNotes({
  row,
  names,
}: Pick<LocationCalculationProps, 'row' | 'names'>) {
  const gt = useGT();
  const servedElsewhere = row.demandWarehouseIds.some(
    (id) => id !== row.warehouseId,
  );
  return (
    <>
      <p className="break-words">
        <T context="stock surplus coverage months">Covered months:</T>{' '}
        {row.coveredMonths.map(month).join(', ') || '—'}
      </p>
      <p className="break-words">
        <T context="stock surplus demand scope">Sales counted at:</T>{' '}
        {namedList(row.demandWarehouseIds, names, gt) || '—'}
      </p>
      {servedElsewhere && (
        <p>
          <T context="stock surplus hub catchment rule">
            Retains the full served catchment’s sales here. Stock already held
            at those locations is not credited against it.
          </T>
        </p>
      )}
    </>
  );
}

function LocationCalculation({
  row,
  names,
  coverageUnverified,
}: LocationCalculationProps) {
  return (
    <div className="space-y-1 border-t pt-2">
      <LocationHeader row={row} names={names} />
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
        <LocationCommitments row={row} />
        <LocationSales row={row} coverageUnverified={coverageUnverified} />
        <LocationProtection row={row} />
      </dl>
      <RecordedSalesCaveat row={row} coverageUnverified={coverageUnverified} />
      <LocationNotes row={row} names={names} />
    </div>
  );
}

/**
 * The always-visible lines under the figure: the arithmetic, the sales window
 * and basis, and whose sales were retained — so the number explains itself
 * without opening the breakdown.
 */
function SurplusSummary({
  result,
  names,
}: {
  result: StockSurplus;
  names: ReadonlyMap<string, string>;
}) {
  const gt = useGT();
  const only = result.locations.length === 1 ? result.locations[0] : null;
  const scope = namedList(demandScopeIds(result), names, gt);
  return (
    <>
      <PanelNote>
        <T context="stock surplus definition">
          Stock above 12-month customer sales and minimum protection, after
          commitments. Diagnostic only; not a transfer limit.
        </T>
      </PanelNote>
      {only &&
      only.available !== null &&
      only.retained !== null &&
      result.quantity !== null ? (
        <PanelNote>
          {gt('{available} after commitments − {retained} retained.', {
            context: 'stock surplus arithmetic, one location',
            available: quantity(only.available),
            retained: quantity(only.retained),
          })}
        </PanelNote>
      ) : null}
      {!only && result.quantity !== null ? (
        <PanelNote>
          {gt(
            'Sum of {count} location results. A shortfall at one location does not offset surplus at another.',
            {
              context: 'stock surplus arithmetic, several locations',
              count: String(result.locations.length),
            },
          )}
        </PanelNote>
      ) : null}
      <PanelNote>
        {gt('Customer sales {start} – {end} · {basis}', {
          context: 'stock surplus sales window and quantity basis',
          start: day(result.windowStart),
          end: lastDayBefore(result.windowEndExclusive),
          basis: surplusSalesBasisLabel(result.salesBasis, gt),
        })}
      </PanelNote>
      {scope ? (
        <PanelNote>
          {gt('Demand scope: {locations}', {
            context: 'stock surplus locations whose sales are retained',
            locations: scope,
          })}
        </PanelNote>
      ) : null}
    </>
  );
}

/** Why the full figure is missing, and what is known without it. */
function UnavailableNote({ result }: { result: StockSurplus }) {
  const gt = useGT();
  const reasons = [
    ...new Set(
      result.locations.flatMap((row) => {
        const label = reasonLabel(row.reason, gt);
        return label ? [label] : [];
      }),
    ),
  ];
  return (
    <PanelNote>
      <T context="stock surplus unavailable warning">
        The full-scope surplus is unavailable. Missing data is not zero.
      </T>
      {reasons.length > 0 ? ` ${reasons.join(' ')}` : null}
      {result.knownSubtotal !== null &&
      result.evaluatedLocations > 0 &&
      result.locations.some((row) => row.status === 'estimated')
        ? ` ${gt(
            'Estimated known subtotal: {subtotal} at {evaluated} of {total} locations — not a full-scope result.',
            {
              context: 'estimated stock surplus partial scope subtotal',
              subtotal: quantity(result.knownSubtotal),
              evaluated: String(result.evaluatedLocations),
              total: String(result.locations.length),
            },
          )}`
        : result.knownSubtotal !== null && result.evaluatedLocations > 0
          ? ` ${gt(
              'Known subtotal: {subtotal} at {evaluated} of {total} locations — not a full-scope result.',
              {
                context: 'stock surplus partial scope subtotal',
                subtotal: quantity(result.knownSubtotal),
                evaluated: String(result.evaluatedLocations),
                total: String(result.locations.length),
              },
            )}`
          : null}
    </PanelNote>
  );
}

function shareValue(result: StockSurplus, gt: Translate): string | null {
  if (result.status === 'unavailable') return null;
  if (result.sharePercent !== null) {
    return `${result.sharePercent.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  }
  return result.available === 0
    ? gt('No available stock', {
        context: 'stock surplus share when nothing is left after commitments',
      })
    : null;
}

function ComposedSurplusFields({
  result,
  locationNames,
}: {
  result: StockSurplus;
  locationNames: ReadonlyMap<string, string>;
}) {
  const gt = useGT();
  return (
    <>
      <dt className="flex min-w-0 items-center gap-1 text-muted-foreground">
        {result.status === 'estimated' ? (
          <T context="composed surplus estimated field label">
            Estimated surplus
          </T>
        ) : (
          <T context="composed surplus field label">Stock surplus</T>
        )}
      </dt>
      <dd className="min-w-0 text-right tabular-nums">
        <SurplusExplanationTrigger
          result={result}
          locationNames={locationNames}
        />
      </dd>
      {result.sharePercent !== null && (
        <Field
          label={
            <T context="composed surplus percentage denominator">
              Share of stock after selected deductions
            </T>
          }
          value={gt('{share}%', {
            context: 'composed surplus share percentage',
            share: result.sharePercent.toLocaleString(undefined, {
              maximumFractionDigits: 1,
            }),
          })}
        />
      )}
      {result.status === 'unavailable' && (
        <PanelNote>
          <T context="composed surplus missing evidence warning">
            Surplus is unavailable. Missing selected source data is not zero.
          </T>
        </PanelNote>
      )}
      <PanelNote>
        <T context="composed surplus diagnostic current data note">
          Uses current stock and selected protection, including on historical
          forecast views. Diagnostic only; this does not authorize transfers.
        </T>
      </PanelNote>
      <div className="col-span-2">
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 px-1 text-xs">
              <CaretDownIcon className="size-4" />
              <T context="composed surplus expanded explanation button">
                How Calculated
              </T>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <SurplusExplanation result={result} locationNames={locationNames} />
          </CollapsibleContent>
        </Collapsible>
      </div>
    </>
  );
}

/**
 * The surplus rows of the incumbent position panel's definition list.
 *
 * Replaces the old "on hand above order-up-to" figure outright: two surplus
 * numbers with different definitions side by side is the ambiguity this field
 * exists to remove.
 */
export function StockSurplusFields({
  result,
  locationNames,
}: {
  result: StockSurplus | null | undefined;
  locationNames: ReadonlyMap<string, string>;
}) {
  const gt = useGT();
  if (result?.settingsIdentity)
    return (
      <ComposedSurplusFields result={result} locationNames={locationNames} />
    );
  const estimated = result?.status === 'estimated';
  const coverageUnverified =
    result?.limitations.includes('customer_sales_coverage_unverified') ?? false;
  const share = result ? shareValue(result, gt) : null;
  const currentDataNote = result?.limitations.includes(
    'current_live_diagnostic',
  )
    ? stockSurplusLimitationLabel('current_live_diagnostic', gt)
    : null;
  return (
    <>
      <Field
        label={
          estimated ? (
            <T context="forecast detail stock surplus label">
              Estimated surplus
            </T>
          ) : (
            <T context="forecast detail stock surplus label">Stock surplus</T>
          )
        }
        value={
          result?.status === 'unavailable'
            ? gt('Unavailable', { context: 'stock surplus status' })
            : result?.quantity
        }
        hint={gt(
          'On hand minus reserved, unallocated sales orders and unacknowledged transfers, then minus the larger of annual customer sales and minimum protection. Never below zero at a location. Incoming orders are not counted.',
          { context: 'stock surplus formula hint' },
        )}
      />
      {share !== null && (
        <Field
          label={
            <T context="stock surplus percentage denominator">
              Share of uncommitted stock
            </T>
          }
          value={share}
        />
      )}
      {estimated && (
        <PanelNote>
          <T context="stock surplus estimate warning">
            Estimate based on partial sales history. Average sales per covered
            month are multiplied by 12. Actual surplus may differ, especially
            for new or seasonal items.
          </T>
        </PanelNote>
      )}
      {result?.status === 'unavailable' && <UnavailableNote result={result} />}
      {currentDataNote && <PanelNote>{currentDataNote}</PanelNote>}
      {result && <SurplusSummary result={result} names={locationNames} />}
      {result && (
        <div className="col-span-2">
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-1 text-xs">
                <CaretDownIcon className="h-4 w-4" />
                <T context="stock surplus disclosure button">How Calculated</T>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-1 text-xs">
              <p>
                <T context="stock surplus formula">
                  Subtract commitments from on-hand stock. Retain the larger of
                  annual customer sales and minimum protection. Stock above that
                  amount is surplus. Incoming orders are excluded.
                </T>
              </p>
              {[...new Set(result.limitations)].flatMap((limitation) =>
                limitation !== 'current_live_diagnostic'
                  ? [
                      <p key={limitation}>
                        {stockSurplusLimitationLabel(limitation, gt)}
                      </p>,
                    ]
                  : [],
              )}
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
                <Field
                  label={
                    <T context="stock surplus sales quantity basis label">
                      Sales basis
                    </T>
                  }
                  value={surplusSalesBasisLabel(result.salesBasis, gt)}
                />
                <Field
                  label={<T context="stock surplus timezone">Timezone</T>}
                  value={result.timezone}
                />
                <Field
                  label={<T context="stock surplus inventory unit">Unit</T>}
                  value={result.unit}
                />
                <Field
                  label={
                    <T context="stock surplus rule provenance">Rule version</T>
                  }
                  value={result.version}
                />
                <Field
                  label={
                    <T context="stock surplus demand catchment date">
                      Demand scope as of
                    </T>
                  }
                  value={result.demandScopeAsOf && day(result.demandScopeAsOf)}
                />
                <Field
                  label={
                    <T context="stock surplus evaluation timestamp">
                      Calculated at
                    </T>
                  }
                  value={dateTimeFormatter.format(new Date(result.evaluatedAt))}
                />
              </dl>
              {result.locations.map((row) => (
                <LocationCalculation
                  key={row.warehouseId}
                  row={row}
                  names={locationNames}
                  coverageUnverified={coverageUnverified}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </>
  );
}
